import React, {useCallback, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import styled from 'styled-components';
import {
	financialStatementCategory,
	defaultDateFormat,
	financialStatementType,
	trialBalanceType,
	misstatementAdjustmentGroup,
	helixImportStatus
} from '../../../../../util/uiconstants';
import Utility from '../../../../../util/utilityfunctions';
import {GetCardTypeForBody, labels} from '../../../../../util/utils';
import {useEngagementId, useUserPreferences} from '../../../../../util/customHooks';
import {useSelectedStEntityId} from '../../../../../util/Ares/customHooks';
import {getTrialBalance, getTrialBalanceById} from '../../../../../actions/TrialBalances/trialbalanceactions';
import {
	getCurrentTrialBalanceByType,
	getFinancialSatementForGrid,
	getPriorPeriodComparativeIsVisible
} from '../../../../../selectors/Ares/trialBalance';
import FinancialStatementAnnotation from '../../../../Common/OAR/FinancialStatementAnnotation';
import BalanceArrow from '../../../OAR/BalanceArrow';
import IconColumn from '../../../OAR/IconColumn';
import NameColumn from '../../../OAR/NameColumn';
import NumberColumn from '../../../OAR/NumberColumn';
import IconColumnHeader from '../../../OAR/IconColumnHeader';
import AresSpecialBodyContainer from './AresSpecialBodyContainer';
import DateColumnHeader from '../../../OAR/DateColumnHeader';
import OARTableWrapper from '../OARTableWrapper';

export default function IncomeStatementTableBody({
	showGuidance,
	guidanceFilter,
	bodyContent,
	headerId,
	sectionId,
	bodyId,
	updateBodyOptions,
	getBodiesBySectionId,
	isSnapshotView
}) {
	const dispatch = useDispatch();
	const engagementId = useEngagementId();
	const formBodyBorder = GetCardTypeForBody(bodyContent);

	const [type, setType] = useState(trialBalanceType.Planning);

	const [selectedStEntityId] = useSelectedStEntityId();

	const modalRef = useRef();

	const userPreferences = useUserPreferences();

	const [trialBalance] = useSelector(getCurrentTrialBalanceByType(type));

	const trialBalanceData = useSelector(
		getFinancialSatementForGrid(
			[financialStatementType.Revenue, financialStatementType.Expense, financialStatementType.NetIncomeExpense],
			trialBalance?.id,
			false,
			undefined,
			true
		)
	);

	const helixConnections = useSelector((store) => store.engagementHelixProjects);

	const priorPeriodComparativeIsVisible = useSelector(
		getPriorPeriodComparativeIsVisible(trialBalance?.id, [
			financialStatementType.Revenue,
			financialStatementType.Expense,
			financialStatementType.NetIncomeExpense
		])
	);

	const formatDate = useCallback(
		(value) => {
			const dateFormat = userPreferences.dateFormat ? userPreferences.dateFormat : defaultDateFormat;

			return moment(value).format(Utility.convertUserPreferencesDateFormatToMoment(dateFormat));
		},
		[userPreferences.dateFormat]
	);

	const openModal = useCallback((showModal, financialStatementId) => {
		modalRef.current?.setShowModal(showModal);
		modalRef.current?.setSelectedFinancialStatementId(financialStatementId);
	}, []);

	const autoGroupColumnDef = useMemo(() => {
		return {
			headerComponent: DateColumnHeader,
			headerComponentParams: {
				label: labels.OARIncomeStatement
			},
			cellRendererParams: {
				suppressCount: true,
				innerRenderer: NameColumn
			},
			minWidth: window.innerWidth > 1600 ? '300' : '250'
		};
	}, []);

	const getDataRow = useCallback(
		(extraQueryParams) => {
			return dispatch(
				getTrialBalance(
					engagementId,
					{
						filterImportStatus: helixImportStatus.importCompleted,
						financialStatementCategory: financialStatementCategory.IncomeStatement,
						multiEntityIds: selectedStEntityId ? selectedStEntityId : undefined,
						...extraQueryParams
					},
					[financialStatementType.Revenue, financialStatementType.Expense, financialStatementType.NetIncomeExpense]
				)
			).then((response) => {
				if (response && response.length > 0) {
					const trialBalanceId = response[0].id;
					return dispatch(
						getTrialBalanceById(
							engagementId,
							trialBalanceId,
							{
								financialStatementCategory: financialStatementCategory.IncomeStatement,
								includedMisstatementAdjustmentGroup: misstatementAdjustmentGroup.Adjustments,
								includeFinancialStatements: true,
								includeFinancialStatementAnnotations: true,
								isNewProvider: true,
								multiEntityIds: selectedStEntityId ? selectedStEntityId : undefined
							},
							[financialStatementType.Revenue, financialStatementType.Expense, financialStatementType.NetIncomeExpense]
						)
					);
				}
			});
		},
		[dispatch, engagementId, selectedStEntityId]
	);

	const columnDefs = useMemo(() => {
		let columns = [
			{
				field: 'analysisPeriodBalance',
				headerComponent: DateColumnHeader,
				headerComponentParams: {
					label: labels.OARCurrentPeriod,
					date: `${formatDate(trialBalance?.currentPeriodDate)}`,
					labelTxt: `${labels.OARColumnA}`
				},
				cellRenderer: 'numberColumn',
				minWidth: window.innerWidth > 1600 ? '100' : '120'
			},
			{
				field: 'comparativePeriodBalance',
				headerComponent: DateColumnHeader,
				headerComponentParams: {
					label: labels.OARComparative1Period,
					date: `${formatDate(trialBalance?.comparativePeriodDate)}`,
					labelTxt: `${labels.OARColumnB}`
				},
				cellRenderer: 'numberColumn',
				minWidth: window.innerWidth > 1600 ? '100' : '120'
			},
			{
				field: 'secondComparativePeriodBalance',
				headerComponent: DateColumnHeader,
				headerComponentParams: {
					label: labels.OARComparative2Period,
					date: `${formatDate(trialBalance?.otherComparativePeriodDate)}`,
					labelTxt: `${labels.OARColumnC}`
				},
				cellRenderer: 'numberColumn',
				hide: !priorPeriodComparativeIsVisible || !trialBalance?.otherComparativePeriodDate,
				minWidth: window.innerWidth > 1600 ? '100' : '120'
			},
			{
				field: 'analysisComparativeBalanceChange',
				headerComponent: DateColumnHeader,
				headerComponentParams: {
					label: labels.OARAmountChangeFrom,
					date: `${formatDate(trialBalance?.comparativePeriodDate)}`,
					labelTxt: `${labels.OARColumnA} - ${labels.OARColumnB}`
				},
				cellRenderer: 'numberColumn',
				minWidth: window.innerWidth > 1600 ? '100' : '120'
			},
			{
				field: 'analysisComparativeBalanceChangePercent',
				headerComponent: DateColumnHeader,
				headerComponentParams: {
					label: labels.OARPercentageChangeFrom,
					date: `${formatDate(trialBalance?.comparativePeriodDate)}`,
					labelTxt: `${labels.OARColumnA} - ${labels.OARColumnB} / ${labels.OARColumnB}`
				},
				cellRenderer: 'balanceArrow',
				minWidth: window.innerWidth > 1600 ? '100' : '120'
			},
			{
				field: 'analysisSecondComparativeBalanceChange',
				headerComponent: DateColumnHeader,
				headerComponentParams: {
					label: labels.OARAmountChangeFrom,
					date: `${formatDate(trialBalance?.otherComparativePeriodDate)}`,
					labelTxt: `${labels.OARColumnA} - ${labels.OARColumnC}`
				},
				cellRenderer: 'numberColumn',
				hide: !priorPeriodComparativeIsVisible || !trialBalance?.otherComparativePeriodDate,
				minWidth: window.innerWidth > 1600 ? '100' : '120'
			},
			{
				field: 'analysisSecondComparativeBalanceChangePercent',
				headerComponent: DateColumnHeader,
				headerComponentParams: {
					label: labels.OARPercentageChangeFrom,
					date: `${formatDate(trialBalance?.otherComparativePeriodDate)}`,
					labelTxt: `${labels.OARColumnA} - ${labels.OARColumnC} / ${labels.OARColumnC}`
				},
				cellRenderer: 'balanceArrow',
				hide: !priorPeriodComparativeIsVisible || !trialBalance?.otherComparativePeriodDate,
				minWidth: window.innerWidth > 1600 ? '100' : '120'
			}
		];

		if (type === trialBalanceType.Planning) {
			columns.push({
				field: 'id',
				headerComponent: IconColumnHeader,
				cellRenderer: 'iconColumn',
				cellRendererParams: {
					onClick: (event, financialStatementId) => {
						openModal(true, financialStatementId);
					}
				},
				maxWidth: window.innerWidth > 1600 ? '120' : '100'
			});
		}

		return columns;
	}, [
		formatDate,
		trialBalance?.currentPeriodDate,
		trialBalance?.comparativePeriodDate,
		trialBalance?.otherComparativePeriodDate,
		priorPeriodComparativeIsVisible,
		type,
		openModal
	]);

	const frameworkComponents = useMemo(() => {
		return {
			balanceArrow: BalanceArrow,
			iconColumn: IconColumn,
			numberColumn: NumberColumn
		};
	}, []);

	return (
		<Wrapper className={`SectionBodyWrapper IncomeStatementTableBody ${formBodyBorder}`}>
			<AresSpecialBodyContainer
				showGuidance={showGuidance}
				guidanceFilter={guidanceFilter}
				bodyContent={bodyContent}
				headerId={headerId}
				sectionId={sectionId}
				bodyId={bodyId}
				updateBodyOptions={updateBodyOptions}
				getBodiesBySectionId={getBodiesBySectionId}
				isSnapshotView={isSnapshotView}
			>
				<OARTableWrapper
					onTypeChange={(type) => setType(type)}
					columnDefs={columnDefs}
					type={type}
					frameworkComponents={frameworkComponents}
					autoGroupColumnDef={autoGroupColumnDef}
					getDataRow={getDataRow}
					trialBalance={trialBalance}
					trialBalanceData={trialBalanceData}
					eyHelixLink={Utility.getOARHelixLink(helixConnections, trialBalance?.trialBalanceAttributeJson, 'income')}
					engagementId={engagementId}
					openModal={openModal}
					showModal={modalRef.current?.show}
				/>
			</AresSpecialBodyContainer>
			<FinancialStatementAnnotation userPreferences={userPreferences} ref={modalRef} />
		</Wrapper>
	);
}

const Wrapper = styled.section`
	display: block;
`;
